import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import { useHistory } from "react-router-dom";

import Breadcrumbs from "../../../../../components/BreadCrumbs";
import Card from "../../../../../components/Card";
import messages from "../../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../../utils/RoutesPaths";

import CompanyHeader from "../../../../../components/Company/CompanyViewDetails/Header/CompanyHeader";
import GeneralInfoSection from "../../../../../components/Company/CompanyEditForm/GeneralInfoSection";
import ContactPersonSection from "../../../../../components/Company/CompanyEditForm/ContactPersonSection";
import CompanyEditDocuments from "../../../../../components/Company/CompanyEditDocuments";
// import { UIButton } from "../../../../../components/ui";

import { fetchCompanyPortalCities } from "../../../../../network/apis/companyPortalRegistration";
import {
  showErrorMsg,
  normalizeDialCode,
  uploadFileWithProgress,
} from "../../../../../utils/Helpers";
import {
  createCompanyEditRequestRequest,
  updateCompanyEditRequestRequest,
  getCompanyEditRequestRequest,
} from "../../../../../store/CompanyPortal/Settings/EditProfile/actions";
import { getMyCompanyRequest } from "../../../../../store/CompanyPortal/MyCompany/actions";
import { ARABIC_REGEX, ENGLISH_REGEX } from "../../../../../utils/Patterns";
import { isCompanyOwner } from "../../../../../utils/Permissions";

import "./_EditCompanyProfile.scss";

const getNationalPhoneNumber = (phoneNumber, dialCode) => {
  const digits = String(phoneNumber ?? "").replace(/\D/g, "");
  const dial = String(dialCode ?? "+966").replace(/\D/g, "");

  return dial && digits.startsWith(dial) ? digits.slice(dial.length) : digits;
};

const EditCompanyProfile = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const { shared, registrationReq, companiesList: cl } = messages[lang];
  const title = shared.companyPortalProfile;

  const data = useSelector((state) => state.auth.login);
  const authCompany = data?.company;

  const editRequest = useSelector(
    (state) => state.companyEditRequest.editRequest,
  );
  const submitting = useSelector(
    (state) => state.companyEditRequest.submitting,
  );
  const myCompany = useSelector((state) => state.companyMyCompany.company);
  const company = myCompany || authCompany;
  const companyOwner = company?.company_owner;
  const owner = companyOwner
    ? {
        ...data,
        ...companyOwner,
        country: companyOwner.country || data?.country,
      }
    : data;

  const activeEditRequestId = myCompany?.company_edit_request?.id ?? null;

  const editRequestForForm =
    activeEditRequestId != null &&
    editRequest &&
    String(editRequest.id) === String(activeEditRequestId)
      ? editRequest
      : null;

  const isFormReady =
    myCompany != null &&
    (activeEditRequestId == null ||
      (editRequestForForm != null &&
        String(editRequestForForm.id) === String(activeEditRequestId)));

  const [isEditMode] = useState(true);
  const [cities, setCities] = useState([]);
  const [pendingDocs, setPendingDocs] = useState({});
  const [uploadState, setUploadState] = useState({
    target: null,
    percent: 0,
  });
  const pendingDocsRef = useRef(pendingDocs);
  pendingDocsRef.current = pendingDocs;

  useEffect(() => {
    dispatch(getMyCompanyRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!isCompanyOwner(data)) {
      history.replace(ROUTE_PATHS.companySettingsProfile);
    }
  }, [data, history]);

  useEffect(() => {
    if (activeEditRequestId == null) return;
    if (String(editRequest?.id) === String(activeEditRequestId)) return;
    dispatch(getCompanyEditRequestRequest({ id: activeEditRequestId }));
  }, [dispatch, activeEditRequestId, editRequest?.id]);

  useEffect(() => {
    if (!isEditMode || cities.length) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchCompanyPortalCities({ page: -1 });
        if (!cancelled) setCities(list || []);
      } catch (error) {
        console.error(error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, cities.length]);

  const initialValues = useMemo(() => {
    const base = {
      name_en: company?.name_en || "",
      name_ar: company?.name_ar || "",
      city_id: company?.city?.id ?? null,
      owner_name: owner?.name || "",
      owner_dial_code:
        owner?.country?.dial_code ||
        company?.company_owner?.country?.dial_code ||
        "+966",
      owner_country_short: (
        owner?.country?.short_code ||
        company?.company_owner?.country?.short_code ||
        "SA"
      ).toLowerCase(),
    };
    base.owner_phone_number = getNationalPhoneNumber(
      owner?.phone_number,
      base.owner_dial_code,
    );

    if (!editRequestForForm) {
      if (activeEditRequestId && myCompany?.company_edit_request) {
        const nested = myCompany.company_edit_request;
        const nestedCityId =
          nested.company_city_id ?? nested.company_city?.id ?? null;
        if (nestedCityId != null) {
          return { ...base, city_id: nestedCityId };
        }
      }
      return base;
    }

    const nextValues = {
      ...base,
      name_en: editRequestForForm.company_name_en ?? base.name_en,
      name_ar: editRequestForForm.company_name_ar ?? base.name_ar,
      city_id:
        editRequestForForm.company_city_id ??
        editRequestForForm.company_city?.id ??
        base.city_id,
      owner_name: editRequestForForm.company_owner_name ?? base.owner_name,
      owner_dial_code:
        editRequestForForm.company_owner_country?.dial_code ||
        base.owner_dial_code,
      owner_country_short: (
        editRequestForForm.company_owner_country?.short_code ||
        base.owner_country_short
      ).toLowerCase(),
    };
    nextValues.owner_phone_number = getNationalPhoneNumber(
      editRequestForForm.company_owner_phone_number ?? base.owner_phone_number,
      nextValues.owner_dial_code,
    );
    return nextValues;
  }, [company, owner, editRequestForForm, myCompany, activeEditRequestId]);

  const editableDocumentUrls = useMemo(() => {
    const base = {
      commercial_record_url: company?.commercial_record_url || "",
      delegation_document_url:
        company?.company_owner?.delegation_document_url ||
        owner?.delegation_document_url ||
        "",
      personal_id_url:
        company?.company_owner?.personal_id_url || owner?.personal_id_url || "",
    };

    if (!editRequestForForm) return base;

    return {
      commercial_record_url:
        editRequestForForm.company_commercial_record_url ??
        base.commercial_record_url,
      delegation_document_url:
        editRequestForForm.company_owner_delegation_document_url ??
        base.delegation_document_url,
      personal_id_url:
        editRequestForForm.company_owner_personal_id_url ?? base.personal_id_url,
    };
  }, [company, owner, editRequestForForm]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues,
    validationSchema: Yup.object({
      name_en: Yup.string()
        .trim()
        .required("enNameRequired")
        .matches(ENGLISH_REGEX, "englishLettersOnly"),
      name_ar: Yup.string()
        .trim()
        .test("arNameOptional", "arabicLettersOnly", (val) => {
          const s = val ?? "";
          return s === "" || ARABIC_REGEX.test(s);
        }),
      city_id: Yup.mixed().required("cityRequired"),
      owner_name: Yup.string().trim().required("nameRequired"),
      owner_phone_number: Yup.string()
        .trim()
        .required("generalFieldRequired")
        // Same rule as the registration ContactForm (BE rejects these with 422).
        .test("phone", "invalidPhone", function (val) {
          const national = String(val ?? "").replace(/\D/g, "");
          const dial = String(this.parent.owner_dial_code ?? "").replace(/\D/g, "");
          if (!/^[1-9]\d{7,14}$/.test(`${dial}${national}`)) return false;
          return dial === "966" ? /^5\d{8}$/.test(national) : true;
        }),
    }),
    onSubmit: async (values) => {
      try {
        const pendingDocsNow = pendingDocsRef.current;

        const commercial_record_url = await uploadFileWithProgress(
          pendingDocsNow.commercial_record_url,
          "commercial_record_url",
          "cr",
          setUploadState,
        );
        const delegation_document_url = await uploadFileWithProgress(
          pendingDocsNow.delegation_document_url,
          "delegation_document_url",
          "delegation",
          setUploadState,
        );
        const personal_id_url = await uploadFileWithProgress(
          pendingDocsNow.personal_id_url,
          "personal_id_url",
          "id_document",
          setUploadState,
        );

        const payload = {
          company_name_en: values.name_en,
          company_name_ar: values.name_ar,
          company_city_id: values.city_id,
          company_owner_name: values.owner_name,
          company_owner_phone_number: String(
            values.owner_phone_number || "",
          ).replace(/\s+/g, ""),
          dial_code: normalizeDialCode(values.owner_dial_code),
        };

        if (commercial_record_url !== undefined) {
          payload.company_commercial_record_url =
            commercial_record_url === null ? "" : commercial_record_url;
        }
        if (delegation_document_url !== undefined) {
          payload.company_owner_delegation_document_url =
            delegation_document_url === null ? "" : delegation_document_url;
        }
        if (personal_id_url !== undefined) {
          payload.company_owner_personal_id_url =
            personal_id_url === null ? "" : personal_id_url;
        }

        const onSuccess = () => {
          setPendingDocs({});
          history.push(ROUTE_PATHS.companySettingsProfile);
        };

        if (activeEditRequestId) {
          dispatch(
            updateCompanyEditRequestRequest({
              id: activeEditRequestId,
              data: payload,
              onSuccess,
            }),
          );
        } else {
          dispatch(
            createCompanyEditRequestRequest({ data: payload, onSuccess }),
          );
        }
      } catch (error) {
        showErrorMsg(error?.message || "Failed to save changes");
      } finally {
        setUploadState({ target: null, percent: 0 });
      }
    },
  });

  // The phone rule depends on the dial code; re-run it once a country switch has committed.
  const { validateForm } = formik;
  useEffect(() => {
    validateForm();
  }, [validateForm, formik.values.owner_dial_code]);

  const uploadProgressLabel =
    uploadState.target != null
      ? `${messages[lang].company.uploadFile.uploading} ${uploadState.percent}%`
      : undefined;

  const formIsLoading = formik.isSubmitting || submitting;
  const hasPendingDocumentChanges = Object.keys(pendingDocs).length > 0;
  const saveDisabled =
    !isFormReady ||
    formIsLoading ||
    !formik.isValid ||
    (!formik.dirty && !hasPendingDocumentChanges);

  const handleCancelEdit = () => {
    if (formIsLoading) return;
    formik.resetForm();
    setPendingDocs({});
    history.push(ROUTE_PATHS.companySettingsProfile);
  };

  const handleSaveEdit = () => {
    if (saveDisabled) return;
    formik.submitForm();
  };

  const handleDocumentChange = (key, fileOrNull) =>
    setPendingDocs((prev) => ({ ...prev, [key]: fileOrNull }));

  // const handleRequestAccountDeletion = () => {
  //   // TODO: wire account-deletion confirmation flow.
  // };

  return (
    <div className="company-portal-profile company-view-details edit-company-profile-page">
      <div className="mt-2">
        <Breadcrumbs
          list={[
            {
              path: "",
              label: shared.settings,
            },
            {
              path: ROUTE_PATHS.companySettingsProfile,
              label: title,
            },
            {
              path: "",
              label: cl.editCompanyProfile,
            },
          ]}
          BreadCrumbHasBackIcon={false}
        />
      </div>

      <CompanyHeader
        company={company}
        hasJoinedDate
        isEditMode={isEditMode}
        hasEditProfileButton={false}
        onCancelEdit={handleCancelEdit}
        onSaveEdit={handleSaveEdit}
        saveDisabled={saveDisabled}
        saveLoading={formIsLoading}
      />

      {isFormReady && (
        <div className="company-portal-profile__layout edit-company-profile-page__layout">
          <div className="company-portal-profile__content">
            <Box
              component="form"
              onSubmit={formik.handleSubmit}
              className="edit-company-profile-page__form company-portal-profile__main"
            >
              <Card
                title={registrationReq.companyInformation}
                classNames={{
                  root: "edit-company-profile-page__card",
                  body: "edit-company-profile-page__card-body",
                }}
              >
                <GeneralInfoSection
                  values={formik.values}
                  setFieldValue={formik.setFieldValue}
                  setFieldTouched={formik.setFieldTouched}
                  cities={cities}
                  errors={formik.errors}
                  touched={formik.touched}
                  commercialRecord={company?.commercial_record || ""}
                  hideTitle
                  disabled={formIsLoading}
                  classNames={{
                    base: "edit-company-profile-page__section",
                    content: "edit-company-profile-page__section-content",
                    inputGroupName: "edit-company-profile-page__name-grid",
                  }}
                />
              </Card>

              <Card
                title={cl.contactPerson}
                classNames={{
                  root: "edit-company-profile-page__card",
                  body: "edit-company-profile-page__card-body",
                }}
              >
                <ContactPersonSection
                  values={formik.values}
                  setFieldValue={formik.setFieldValue}
                  setFieldTouched={formik.setFieldTouched}
                  errors={formik.errors}
                  touched={formik.touched}
                  ownerEmail={owner?.email || ""}
                  hideTitle
                  disabled={formIsLoading}
                  classNames={{
                    base: "edit-company-profile-page__section",
                    inputGroupName: "edit-company-profile-page__contact-grid",
                  }}
                />
              </Card>
{/* 
              <div className="edit-company-profile-page__delete">
                <UIButton
                  type="button"
                  variant="destructive"
                  appearance="outline"
                  size="medium"
                  onClick={handleRequestAccountDeletion}
                  disabled={formIsLoading}
                >
                  {cl.requestAccountDeletion}
                </UIButton>
              </div> */}
            </Box>
          </div>

          <aside className="company-portal-profile__aside">
            <CompanyEditDocuments
              commercialRecordUrl={editableDocumentUrls.commercial_record_url}
              delegationDocumentUrl={
                editableDocumentUrls.delegation_document_url
              }
              idDocumentUrl={editableDocumentUrls.personal_id_url}
              pendingDocs={pendingDocs}
              onDocumentChange={handleDocumentChange}
              uploadState={uploadState}
              uploadProgressLabel={uploadProgressLabel}
              disabled={formIsLoading}
              classNames={{ root: "edit-company-profile-page__documents" }}
            />
          </aside>
        </div>
      )}
    </div>
  );
};

export default EditCompanyProfile;
